import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import DietitianLogin from "@/pages/DietitianLogin";
import AdminLogin from "@/pages/AdminLogin";
import ResetPassword from "@/pages/ResetPassword";
import DietitianLayout from "@/components/layout/DietitianLayout";
import PatientLayout from "@/components/layout/PatientLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import DietitianDashboard from "@/pages/dietitian/Dashboard";
import DietitianProfile from "@/pages/dietitian/Profile";
import Patients from "@/pages/dietitian/Patients";
import PatientDetail from "@/pages/dietitian/PatientDetail";
import Foods from "@/pages/dietitian/Foods";
import Recipes from "@/pages/dietitian/Recipes";
import RecipeForm from "@/pages/dietitian/RecipeForm";
import DietPlans from "@/pages/dietitian/DietPlans";
import DietPlanForm from "@/pages/dietitian/DietPlanForm";
import PatientToday from "@/pages/patient/Today";
import PatientProgress from "@/pages/patient/Progress";
import PatientRecipes from "@/pages/patient/Recipes";
import PatientProfile from "@/pages/patient/Profile";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminDietitians from "@/pages/admin/Dietitians";
import AdminBranding from "@/pages/admin/Branding";
import AdminIntegrations from "@/pages/admin/Integrations";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="loading-screen">
        Loading…
      </div>
    );
  }
  if (!user) {
    const target = roles?.includes("patient") ? "/login" : "/dietitian/login";
    return <Navigate to={target} state={{ from: location }} replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={defaultRouteForRole(user.role)} replace />;
  }
  return children;
}

function defaultRouteForRole(role) {
  if (role === "patient") return "/me/today";
  if (role === "super_admin") return "/admin";
  return "/dashboard";
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultRouteForRole(user.role)} replace />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/dietitian/login" element={<DietitianLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/contact" element={<Contact />} />

      {/* Super admin */}
      <Route element={<ProtectedRoute roles={["super_admin"]}><AdminLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/dietitians" element={<AdminDietitians />} />
        <Route path="/admin/branding" element={<AdminBranding />} />
        <Route path="/admin/integrations" element={<AdminIntegrations />} />
      </Route>

      {/* Dietitian */}
      <Route element={<ProtectedRoute roles={["dietitian"]}><DietitianLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DietitianDashboard />} />
        <Route path="/profile" element={<DietitianProfile />} />
        <Route path="/patients" element={<Patients />} />
        <Route path="/patients/:id" element={<PatientDetail />} />
        <Route path="/foods" element={<Foods />} />
        <Route path="/recipes" element={<Recipes />} />
        <Route path="/recipes/new" element={<RecipeForm />} />
        <Route path="/recipes/:id" element={<RecipeForm />} />
        <Route path="/diet-plans" element={<DietPlans />} />
        <Route path="/diet-plans/new" element={<DietPlanForm />} />
        <Route path="/diet-plans/:id" element={<DietPlanForm />} />
      </Route>

      {/* Patient */}
      <Route element={<ProtectedRoute roles={["patient"]}><PatientLayout /></ProtectedRoute>}>
        <Route path="/me/today" element={<PatientToday />} />
        <Route path="/me/progress" element={<PatientProgress />} />
        <Route path="/me/recipes" element={<PatientRecipes />} />
        <Route path="/me/profile" element={<PatientProfile />} />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <SettingsProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </SettingsProvider>
    </ErrorBoundary>
  );
}

export default App;
